/**
 * Sync Queue Manager
 *
 * Manages a queue of operations to be synced with the server
 * when the network becomes available.
 */

import {
  STORES,
  setItem,
  getItem,
  deleteItem,
  getAllItems,
  type SyncQueueItem,
} from './storage';
import { tasksApi } from '@services/api/tasks';

// Generate a unique ID for queue items
function generateQueueId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Event emitter for sync status updates
type SyncEventHandler = (event: SyncEvent) => void;
const eventHandlers: Set<SyncEventHandler> = new Set();

export interface SyncEvent {
  type: 'sync_start' | 'sync_complete' | 'sync_error' | 'queue_change';
  queueLength: number;
  error?: string;
  syncedCount?: number;
}

/**
 * Subscribe to sync events
 */
export function onSyncEvent(handler: SyncEventHandler): () => void {
  eventHandlers.add(handler);
  return () => eventHandlers.delete(handler);
}

/**
 * Emit sync event to all subscribers
 */
function emitSyncEvent(event: SyncEvent): void {
  eventHandlers.forEach((handler) => handler(event));
}

/**
 * Add an item to the sync queue
 */
export async function addToSyncQueue(
  type: SyncQueueItem['type'],
  entityType: SyncQueueItem['entityType'],
  entityId: string,
  payload: Record<string, unknown>
): Promise<string> {
  const item: SyncQueueItem = {
    id: generateQueueId(),
    type,
    entityType,
    entityId,
    payload,
    createdAt: Date.now(),
    retryCount: 0,
  };

  await setItem(STORES.SYNC_QUEUE, item);

  const queue = await getAllItems<SyncQueueItem>(STORES.SYNC_QUEUE);
  emitSyncEvent({
    type: 'queue_change',
    queueLength: queue.length,
  });

  return item.id;
}

/**
 * Remove an item from the sync queue
 */
export async function removeFromSyncQueue(id: string): Promise<void> {
  await deleteItem(STORES.SYNC_QUEUE, id);

  const queue = await getAllItems<SyncQueueItem>(STORES.SYNC_QUEUE);
  emitSyncEvent({
    type: 'queue_change',
    queueLength: queue.length,
  });
}

/**
 * Get the current sync queue
 */
export async function getSyncQueue(): Promise<SyncQueueItem[]> {
  const items = await getAllItems<SyncQueueItem>(STORES.SYNC_QUEUE);
  // Sort by creation time (oldest first)
  return items.sort((a, b) => a.createdAt - b.createdAt);
}

/**
 * Get the sync queue length
 */
export async function getSyncQueueLength(): Promise<number> {
  const items = await getAllItems<SyncQueueItem>(STORES.SYNC_QUEUE);
  return items.length;
}

/**
 * Update retry count and error for a queue item
 */
export async function updateQueueItem(
  id: string,
  updates: Partial<Pick<SyncQueueItem, 'retryCount' | 'lastError'>>
): Promise<void> {
  const item = await getItem<SyncQueueItem>(STORES.SYNC_QUEUE, id);
  if (item) {
    await setItem(STORES.SYNC_QUEUE, { ...item, ...updates });
  }
}

/**
 * Process a single queue item
 */
async function processQueueItem(item: SyncQueueItem): Promise<boolean> {
  try {
    switch (item.entityType) {
      case 'task':
        await processTaskOperation(item);
        break;
      // Add other entity types as needed
      default:
        console.warn(`Unknown entity type: ${item.entityType}`);
        return false;
    }

    // Successfully processed - remove from queue
    await removeFromSyncQueue(item.id);
    return true;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error(`Failed to sync item ${item.id}:`, errorMessage);

    // Update retry count
    await updateQueueItem(item.id, {
      retryCount: item.retryCount + 1,
      lastError: errorMessage,
    });

    // Remove if too many retries
    if (item.retryCount >= 5) {
      console.error(`Removing item ${item.id} after ${item.retryCount} failures`);
      await removeFromSyncQueue(item.id);
    }

    return false;
  }
}

/**
 * Process task-specific operations
 */
async function processTaskOperation(item: SyncQueueItem): Promise<void> {
  const { type, entityId, payload } = item;

  switch (type) {
    case 'create': {
      // Build create request, only including defined properties
      const createRequest: Parameters<typeof tasksApi.create>[0] = {
        boardId: payload.boardId as string,
        title: payload.title as string,
      };
      if (payload.description) {
        createRequest.description = payload.description as string;
      }
      if (payload.priority) {
        createRequest.priority = payload.priority as 'low' | 'medium' | 'high' | 'urgent' | 'none';
      }
      if (payload.dueDate) {
        createRequest.dueDate = payload.dueDate as string;
      }
      if (payload.assigneeId) {
        createRequest.assigneeId = payload.assigneeId as string;
      }
      await tasksApi.create(createRequest);
      break;
    }

    case 'update': {
      // Build update request, only including defined properties
      const updateRequest: Parameters<typeof tasksApi.update>[1] = {};
      if (payload.title) {
        updateRequest.title = payload.title as string;
      }
      if (payload.description) {
        updateRequest.description = payload.description as string;
      }
      // Note: Task status is managed via columnId changes (moving between columns)
      if (payload.columnId) {
        updateRequest.columnId = payload.columnId as string;
      }
      if (payload.priority) {
        updateRequest.priority = payload.priority as 'low' | 'medium' | 'high' | 'urgent' | 'none';
      }
      if (payload.dueDate !== undefined) {
        updateRequest.dueDate = payload.dueDate as string | null;
      }
      if (payload.assigneeId !== undefined) {
        updateRequest.assigneeId = payload.assigneeId as string | null;
      }
      await tasksApi.update(entityId, updateRequest);
      break;
    }

    case 'delete':
      // Delete task on server
      await tasksApi.delete(entityId);
      break;

    default:
      throw new Error(`Unknown operation type: ${type}`);
  }
}

// Sync state
let isSyncing = false;

/**
 * Process the entire sync queue
 */
export async function processSyncQueue(): Promise<{
  success: boolean;
  synced: number;
  failed: number;
}> {
  if (isSyncing) {
    console.log('Sync already in progress');
    return { success: false, synced: 0, failed: 0 };
  }

  // Check if online
  if (!navigator.onLine) {
    console.log('Offline - skipping sync');
    return { success: false, synced: 0, failed: 0 };
  }

  isSyncing = true;
  const queue = await getSyncQueue();

  emitSyncEvent({
    type: 'sync_start',
    queueLength: queue.length,
  });

  let synced = 0;
  let failed = 0;

  for (const item of queue) {
    const success = await processQueueItem(item);
    if (success) {
      synced++;
    } else {
      failed++;
    }
  }

  isSyncing = false;

  const finalQueue = await getSyncQueue();
  emitSyncEvent({
    type: 'sync_complete',
    queueLength: finalQueue.length,
    syncedCount: synced,
  });

  return {
    success: failed === 0,
    synced,
    failed,
  };
}

/**
 * Get sync status
 */
export function isSyncInProgress(): boolean {
  return isSyncing;
}

// ============================================================================
// Network status listeners
// ============================================================================

let networkListenerSetup = false;

/**
 * Setup network status listeners for automatic sync
 */
export function setupNetworkListeners(): void {
  if (networkListenerSetup) return;
  networkListenerSetup = true;

  // Sync when coming back online
  window.addEventListener('online', async () => {
    console.log('Network online - starting sync');
    const queue = await getSyncQueue();
    if (queue.length > 0) {
      await processSyncQueue();
    }
  });

  // Log when going offline
  window.addEventListener('offline', () => {
    console.log('Network offline - operations will be queued');
  });

  // Sync when page becomes visible
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      const queue = await getSyncQueue();
      if (queue.length > 0) {
        await processSyncQueue();
      }
    }
  });
}

// ============================================================================
// Conflict resolution
// ============================================================================

export interface ConflictResolution {
  strategy: 'local' | 'server' | 'merge' | 'manual';
  resolvedData?: Record<string, unknown>;
}

/**
 * Simple conflict resolution - server wins by default
 */
export function resolveConflict(
  localData: Record<string, unknown>,
  serverData: Record<string, unknown>,
  localTimestamp: number,
  serverTimestamp: number
): ConflictResolution {
  // If server is newer, use server data
  if (serverTimestamp > localTimestamp) {
    return { strategy: 'server', resolvedData: serverData };
  }

  // If local is newer, use local data
  return { strategy: 'local', resolvedData: localData };
}
